import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from 'react';

import { FCWithChildren } from 'common/types';
import { useModal } from 'components/ModalProvider/hooks';

import DeleteContactModal from '../../pages/Details/components/DeleteContactModal';
import EditContactModal from '../../pages/Details/components/EditContactModal';
import { Contact } from '../../pages/Details/types';
import { ClientFromList } from '../../pages/List/hooks/queries/useGetClients';
import ClientFormModal from '../FormModal';
import { ClientModalType, NewClientType } from './types';

export interface ClientModalContextState {
  contact: Contact | null;
  setContact: Dispatch<SetStateAction<Contact | null>>;
  client: ClientFromList | NewClientType | null;
  setClient: Dispatch<SetStateAction<ClientFromList | NewClientType | null>>;
  hideModal: () => void;
}

export const ClientModalContext = createContext<ClientModalContextState | null>(
  null,
);

export const ClientModalProvider: FCWithChildren = ({ children }) => {
  const [client, setClient] = useState<ClientModalContextState['client']>(null);
  const [contact, setContact] =
    useState<ClientModalContextState['contact']>(null);

  const { hideModal, isModalOpen } = useModal();

  return (
    <ClientModalContext.Provider
      value={{
        client,
        contact,
        hideModal,
        setClient,
        setContact,
      }}
    >
      {children}

      {isModalOpen(ClientModalType.EditContact) && contact ? (
        <EditContactModal contact={contact} />
      ) : null}
      {isModalOpen(ClientModalType.DeleteContact) && contact ? (
        <DeleteContactModal contact={contact} />
      ) : null}
      {isModalOpen(ClientModalType.CreateEdit) ? (
        <ClientFormModal client={client} />
      ) : null}
    </ClientModalContext.Provider>
  );
};

export const useClientModalContext = () => {
  const context = useContext(ClientModalContext);
  if (!context) {
    throw new Error(
      'useClientModalContext must be used within a ClientModalContextProvider',
    );
  }
  return context;
};
